import {useMemo} from 'react';

import {useProjectParams} from '../../../pages/ProjectPage/useProjectParams';
import {mergeViz, patchVizMap} from '../../../state/graphql/historyKeysQuery';
import {useActiveHistoryKeyInfo} from '../../../state/panelGenHistoryKeys/useActiveHistoryKeyInfo';
import {type ExpectedPanelSpecsInputs} from '../../../util/panelbank';
import {useProjectRsaReadEnabledWithLoading} from '../../../util/rampFeatureFlags/projectRsaReadEnabled.query';
import {
  useStatsigGateEvalTables,
  useStatsigGateEvalTablesCes,
} from '../../../util/rampFeatureFlags/rampFeatureFlags';
import {useCheckWeaveSignal} from '../../../util/useWeaveTrace';
import {useAutoOrganizePrefix} from '../../WorkspaceDrawer/Settings/hooks';
import {useDefinedMetricContext} from '../contextProviders/DefinedMetricContext';
import {useWandbConfigContext} from '../contextProviders/WandbConfigContext';

/**
 * The expected panel spec inputs for the current workspace, or null until the
 * history keys and wandb config have both loaded, or when either failed.
 * Must be called inside the workspace's `WandbConfigContextProvider`.
 */
export function useExpectedPanelSpecsInputs(): ExpectedPanelSpecsInputs | null {
  const wbConfigQuery = useWandbConfigContext();
  // Expected panels come from the panel-gen sample only. The visibility keys
  // answer whether displayed metrics have data and must not generate panels
  // of their own.
  const {
    historyKeyInfo,
    loading: activeHistoryKeysLoading,
    error: historyKeysError,
  } = useActiveHistoryKeyInfo({isPanelGen: true});

  // Wait for the accelerator-promotion answer, so we don't briefly derive
  // panels from the legacy history-keys query on a project that turns out to
  // be running NGW.
  const routeProject = useProjectParams();
  const {loading: isNgwLoading} = useProjectRsaReadEnabledWithLoading(
    routeProject.entityName,
    routeProject.projectName
  );
  const historyKeysLoading = isNgwLoading || activeHistoryKeysLoading;

  const autoOrganizePrefix = useAutoOrganizePrefix();
  const {definedMetricsMap, hiddenMetrics} = useDefinedMetricContext();
  // Heuristic: include Weave Trace panel only if project has trace data
  const shouldIncludeWeavePanel = useCheckWeaveSignal();
  const isEvalTablesEnabled = useStatsigGateEvalTables() ?? false;
  const isEvalTablesCesEnabled = useStatsigGateEvalTablesCes();

  return useMemo(
    function memoExpectedPanelSpecsInputs() {
      if (
        historyKeysLoading ||
        historyKeysError ||
        wbConfigQuery.loading ||
        wbConfigQuery.error ||
        !wbConfigQuery.wandbConfig
      ) {
        return null;
      }
      return {
        keys: historyKeyInfo.keys,
        viz: patchVizMap(mergeViz(wbConfigQuery.wandbConfig)),
        autoOrganizePrefix,
        definedMetricsMap,
        hiddenMetrics,
        shouldIncludeWeavePanel,
        isEvalTablesEnabled,
        isEvalTablesCesEnabled,
      };
    },
    [
      historyKeysLoading,
      historyKeysError,
      wbConfigQuery,
      historyKeyInfo.keys,
      autoOrganizePrefix,
      definedMetricsMap,
      hiddenMetrics,
      shouldIncludeWeavePanel,
      isEvalTablesEnabled,
      isEvalTablesCesEnabled,
    ]
  );
}
